/**
 * THE HAND ATTACHMENT SEAM — how a prop rides an ANIMATED bone. (§6, §4.5)
 *
 * ── The defect this exists to close ────────────────────────────────────────
 *
 * `stepHeldBall` (sim/goalkeeper.ts) says "glue it to his hands" and then asks
 * `computeSocket` for `ContactSocket.Hands`. Every offset in that table is
 * built in the player's UPRIGHT frame — forward × facing, right × facing−90°,
 * up × world-up, all measured from his feet. That is exactly right for a
 * server with no skeleton, and it is the reason the sim can stay authoritative.
 * It also means A SOCKET CANNOT FOLLOW A POSE. A diving keeper's "hands" are
 * still 1.18 m straight up from wherever his body is, so
 * `MODELS_SHOWCASE/1104_GKHANDS_hold--hands.png` shows the ball floating a foot
 * above and clear of the outstretched glove.
 *
 * ⚠ THIS IS NOT A KEEPER BUG. Any socket-placed prop is pose-blind the moment
 * its owner dives, slides or falls. The seam is therefore general: a provider
 * publishes hand anchors for a slot, a consumer asks for one.
 *
 * ── The contract, and why the sim never learns about it ────────────────────
 *
 * The sim is and stays THE authority for `ball.px/py/pz`. Possession, physics,
 * the wire and the hash all read that and nothing here writes back into a
 * WorldState (§15.2) — the render layer never even sees one. What this seam
 * produces is a PRESENTATION CORRECTION applied to the drawn mesh only, and it
 * is deliberately bounded (see `HOLD_MAX_CORRECTION_M` in `ball.ts`): the drawn
 * ball may sit up to an arm's length from the simulated ball WHILE HELD, and
 * never anywhere else. That divergence IS the fix — it is the pose-blindness of
 * an upright-frame socket being corrected by the pose that is actually on
 * screen — and bounding it is what keeps it from becoming a second source of
 * truth. Outside a hold the correction decays to exactly zero.
 *
 * ── Why a registry and not an import ──────────────────────────────────────
 *
 * `subsystems.ts` promises that subsystems "never import each other". The ball
 * subsystem needs a point that only a player subsystem can compute, and there
 * are three player subsystems (cast, shared master, debug capsules) of which
 * exactly one is ever constructed. So both sides hold THIS module instead, via
 * `RenderContext.attachments` — the provider is whoever inits with a rig, and a
 * consumer that asks when nobody has published simply gets `false` and falls
 * back to the sim position. Capsules have no hands and publish nothing; that is
 * a supported configuration, not an error.
 */

import { Vector3, type Object3D, type SkinnedMesh } from 'three';

/**
 * WHICH HAND. A keeper who has gathered a ball two-handed must have it between
 * the gloves, not stuck to one of them — that is `BothHands`, and it is what
 * every held state in the game currently is.
 *
 * ⚠ NO HANDEDNESS EXISTS TO READ. The sim picks a contact FOOT geometrically
 * (`contactFootFor` → whichever side the ball is on); there is no
 * preferred-foot or preferred-hand attribute anywhere in `WorldState` or the
 * attribute table to build a one-handed throw from. `LeftHand`/`RightHand` are
 * defined because the geometry supports them the moment a state earns one — a
 * one-handed parry or an overarm throw whose release frame is authored — and
 * are deliberately unused until then rather than guessed at.
 */
export const enum BallHoldPose {
  /** Nobody is holding it. */
  None = 0,
  /** Gathered/carried/smothered, or a Law 15 two-handed throw-in. */
  BothHands = 1,
  LeftHand = 2,
  RightHand = 3,
}

/**
 * One player's hands, resolved once at rig build.
 *
 * `wrist` is the deform bone the arm chain ends on (`hand_l` / `hand_r` on the
 * 74-bone rig). `knuckle` is the middle finger's first joint when the rig has
 * one — the wrist joint sits at the base of the palm, so a ball parented
 * straight to it rides the heel of the hand rather than the palm. The palm
 * point is the midpoint of the two, which is stable under any pose because it
 * is defined by the bones themselves and not by an offset in some frame.
 */
export interface HandRig {
  readonly leftWrist: Object3D | null;
  readonly leftKnuckle: Object3D | null;
  readonly rightWrist: Object3D | null;
  readonly rightKnuckle: Object3D | null;
}

export const EMPTY_HAND_RIG: HandRig = {
  leftWrist: null, leftKnuckle: null, rightWrist: null, rightKnuckle: null,
};

/** Does this rig expose at least one hand? */
export function hasHands(rig: HandRig): boolean {
  return rig.leftWrist !== null || rig.rightWrist !== null;
}

/**
 * Bone names, in priority order, lowercased and with any `armature:`/
 * `mixamorig5:` style prefix stripped.
 *
 * EXACT matches only. A substring match on "hand" would bind the wrist to
 * `LeftHandMiddle1`, and a fuzzy match is exactly how a rig change turns into a
 * silently-wrong attachment months later. A rig that renames its hands fails
 * loudly (no anchor → the ball stays where the sim put it) instead.
 *
 * The 74-bone production rig is `hand_l`/`hand_r` + `middle_01_l`/`middle_01_r`
 * (assets/player-master; see `authoredSkeletonContract.ts`). The other entries
 * are the temporary Mixamo test rig and the clean-room PlayerV1 vocabulary, so
 * this file does not have to change when a bug hunt swaps the skeleton.
 */
const LEFT_WRIST = ['hand_l', 'hand.l', 'l_hand', 'lefthand', 'hand_socket_l', 'socket_hand_l'];
const RIGHT_WRIST = ['hand_r', 'hand.r', 'r_hand', 'righthand', 'hand_socket_r', 'socket_hand_r'];
const LEFT_KNUCKLE = ['middle_01_l', 'middle01_l', 'middle_l', 'lefthandmiddle1'];
const RIGHT_KNUCKLE = ['middle_01_r', 'middle01_r', 'middle_r', 'righthandmiddle1'];

function normalize(name: string): string {
  const colon = name.lastIndexOf(':');
  return (colon >= 0 ? name.slice(colon + 1) : name).toLowerCase();
}

function pick(byName: Map<string, Object3D>, candidates: readonly string[]): Object3D | null {
  for (const c of candidates) {
    const hit = byName.get(c);
    if (hit) return hit;
  }
  return null;
}

/**
 * Resolve a rig's hands from its bone list. Called ONCE per instantiated
 * skeleton (per cast character per LOD tier, or per master slot) — never per
 * frame, because a name lookup over 74 bones × 22 players × 60 Hz is real cost
 * for an answer that cannot change.
 */
export function findHandRig(bones: readonly Object3D[]): HandRig {
  const byName = new Map<string, Object3D>();
  for (const b of bones) {
    const n = normalize(b.name);
    // First wins: a rig with duplicate bone names is already broken, and
    // preferring the first keeps the result stable across instantiations.
    if (!byName.has(n)) byName.set(n, b);
  }
  return {
    leftWrist: pick(byName, LEFT_WRIST),
    leftKnuckle: pick(byName, LEFT_KNUCKLE),
    rightWrist: pick(byName, RIGHT_WRIST),
    rightKnuckle: pick(byName, RIGHT_KNUCKLE),
  };
}

/**
 * A player subsystem's published view of its own slots.
 *
 * Returns `false` when this slot has no rig to read — not loaded yet, drawn as
 * a capsule, or an LOD tier that has never been posed. A consumer must treat
 * that as "leave the prop where the sim put it", never as an error.
 */
export interface HandAnchorProvider {
  /**
   * World-space hold point for `slot`, written into `out`.
   *
   * `near` is the SIM's position for the prop. It is passed in rather than
   * inferred because it is what disambiguates a two-handed pose: the candidate
   * set is {left palm, right palm, the midpoint between them} and the one
   * closest to where the sim says the ball is wins. Standing, the hands are
   * together in front of the chest and the midpoint wins — the ball sits
   * BETWEEN the gloves, which is how a gathered ball is held. Mid-dive the arms
   * are not together and the midpoint is somewhere in his chest, so the
   * outstretched glove wins. One rule, and the dive case falls out of it
   * instead of needing a special case that would then be wrong for slides and
   * falls too.
   */
  handAnchor(slot: number, pose: BallHoldPose, near: Vector3, out: Vector3): boolean;
}

/** The seam itself. One per `Renderer`, handed to every subsystem via context. */
/**
 * THE SECOND CHANNEL: a slot's currently-drawn SKINNED MESHES. (boostAura.ts)
 *
 * The hand seam above answers "where is his palm". This one answers "what
 * geometry is he being drawn with right now", which is what an effect that has
 * to hug an ANIMATED SILHOUETTE needs — an inverted-hull aura binds a clone of
 * these meshes to the same skeleton, so it follows every pose for free instead
 * of trying to approximate a body with a primitive.
 *
 * ⚠ THE RETURNED ARRAY'S IDENTITY IS THE CONTRACT, and it is what makes this
 * safe against the trap `castPlayerVisual.ts`'s header documents twice: every
 * LOD tier owns its own instance, and tiers are swapped by camera distance, so
 * anything bound to the meshes of the tier a slot HAPPENS to be drawing is stale
 * the moment the camera crosses a band. A provider must therefore return the
 * per-instance array itself (stable while that tier is drawn, a DIFFERENT array
 * object after a swap) and never a fresh array each call. A consumer compares it
 * by identity and rebuilds when it changes — which is how an effect ends up
 * per-SLOT while its geometry is necessarily per-tier.
 *
 * Returns null when the slot is not drawn, or when this subsystem has no skinned
 * geometry to offer (the debug capsules); the consumer then draws nothing, which
 * is the right answer rather than a guess.
 */
export interface SilhouetteProvider {
  silhouette(slot: number): readonly SkinnedMesh[] | null;
}

export interface AttachmentRegistry {
  /** A player subsystem publishes at init; `null` at dispose. */
  setHandProvider(provider: HandAnchorProvider | null): void;
  /** A consumer asks. `false` = no anchor available; use the sim position. */
  handAnchor(slot: number, pose: BallHoldPose, near: Vector3, out: Vector3): boolean;
  /** A player subsystem publishes at init; `null` at dispose. */
  setSilhouetteProvider(provider: SilhouetteProvider | null): void;
  /** A consumer asks. `null` = nothing drawn for that slot; draw nothing. */
  silhouette(slot: number): readonly SkinnedMesh[] | null;
}

export function createAttachmentRegistry(): AttachmentRegistry {
  let provider: HandAnchorProvider | null = null;
  let silhouettes: SilhouetteProvider | null = null;
  return {
    setHandProvider(p) {
      provider = p;
    },
    handAnchor(slot, pose, near, out) {
      if (provider === null || pose === BallHoldPose.None) return false;
      return provider.handAnchor(slot, pose, near, out);
    },
    setSilhouetteProvider(p) {
      silhouettes = p;
    },
    silhouette(slot) {
      return silhouettes === null ? null : silhouettes.silhouette(slot);
    },
  };
}

/**
 * The shared implementation both player subsystems delegate to, so the cast and
 * the shared master cannot drift in where a ball sits.
 *
 * ⚠ `updateWorldMatrix(true, false)` IS NOT OPTIONAL. `AnimationMixer.update`
 * writes bone LOCAL transforms only, and the sole `scene.updateMatrixWorld`
 * pass runs inside `gl.render()` — i.e. AFTER every subsystem's `update`. The
 * player subsystem also writes its root's position in the same pass. So at the
 * moment the ball subsystem asks, every `matrixWorld` on the chain is one frame
 * stale, and a ball read from a stale matrix trails the hand by exactly one
 * frame of body movement — which is failure mode 1, "the ball drifts off the
 * hand", arriving through the back door. Walking the parents costs ~6 matrix
 * multiplies for the one chain we actually read.
 */
export function handAnchorFromRig(
  rig: HandRig,
  pose: BallHoldPose,
  near: Vector3,
  out: Vector3,
  scratchA: Vector3,
  scratchB: Vector3,
): boolean {
  const wantLeft = pose === BallHoldPose.BothHands || pose === BallHoldPose.LeftHand;
  const wantRight = pose === BallHoldPose.BothHands || pose === BallHoldPose.RightHand;
  const left = wantLeft ? palmPoint(rig.leftWrist, rig.leftKnuckle, scratchA) : null;
  const right = wantRight ? palmPoint(rig.rightWrist, rig.rightKnuckle, scratchB) : null;

  if (left !== null && right !== null) {
    // Three candidates; the one nearest the sim's ball wins. See the doc on
    // `HandAnchorProvider.handAnchor` for why this is the whole pose rule.
    const dL = left.distanceToSquared(near);
    const dR = right.distanceToSquared(near);
    const mx = (left.x + right.x) * 0.5;
    const my = (left.y + right.y) * 0.5;
    const mz = (left.z + right.z) * 0.5;
    const dM = (mx - near.x) ** 2 + (my - near.y) ** 2 + (mz - near.z) ** 2;
    if (dM <= dL && dM <= dR) out.set(mx, my, mz);
    else if (dL <= dR) out.copy(left);
    else out.copy(right);
    return true;
  }
  if (left !== null) {
    out.copy(left);
    return true;
  }
  if (right !== null) {
    out.copy(right);
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// THE HOLD ITSELF — one offset, three failure modes
// ---------------------------------------------------------------------------

/**
 * Tuning for `createHeldOffsetTracker`. All four numbers are argued for at the
 * `HOLD_*` constants in `ball.ts`, which is the only caller that sets them;
 * they live in the signature so the tracker stays a pure, testable mechanism
 * with no opinions about balls.
 */
export interface HeldOffsetTuning {
  /** Cap on |drawn − simulated| while held. Beyond it, the hold is refused. */
  maxCorrectionM: number;
  /** Seconds; how fast the prop settles INTO the hand. */
  gatherTauS: number;
  /** Seconds; how fast the offset returns to zero once released. */
  releaseTauS: number;
  /** Cap on the offset seeded at the moment a hold begins. */
  seedMaxM: number;
}

export interface HeldOffsetTracker {
  /**
   * Advance one frame and return the position to DRAW at.
   *
   * `simPos` is authoritative and untouched. `hand` is the anchor, or null when
   * nothing is holding it / no anchor was available. `drawnPos` is where the
   * prop was drawn last frame, which is what makes the catch frame continuous.
   *
   * The returned vector is internal scratch — read it, do not keep it.
   */
  step(simPos: Vector3, hand: Vector3 | null, holder: number, dt: number, drawnPos: Vector3): Vector3;
  /** True while the prop is actually riding a hand. Diagnostics/tests. */
  readonly attached: boolean;
  /** Current |drawn − simulated|. Diagnostics/tests. */
  readonly correctionM: number;
}

/**
 * THE HOLD, AS ONE ACCUMULATED OFFSET.
 *
 * Carrying `drawn − simulated` rather than a mode flag plus a position is what
 * makes all three named failure modes fall out of the same few lines:
 *
 *   NOT PARENTED — the target is recomputed from the caller's live bone anchor
 *     every frame, never cached and never expressed in the holder's frame, so
 *     the prop follows the hand through the pose instead of sitting where the
 *     hand was.
 *   SNAPS RATHER THAN GATHERS — on the frame a hold begins the offset is SEEDED
 *     with whatever leaves the drawn position exactly where it already was. The
 *     sim's claim is instantaneous; the picture is not, and the prop eases into
 *     the glove from wherever it was flying.
 *   POSE-BLIND — there is no pose in here at all. The anchor is a bone in world
 *     space, so diving, sliding or fallen is the same code.
 *
 * And the invariant that keeps it from becoming a second source of truth: with
 * no hold the offset decays to EXACTLY zero, so a loose prop is drawn at the
 * simulated position and nothing else in the game can disagree with it.
 */
export function createHeldOffsetTracker(t: HeldOffsetTuning): HeldOffsetTracker {
  const offset = new Vector3();
  const target = new Vector3();
  const out = new Vector3();
  let heldBy = -1;
  let attached = false;
  const maxSq = t.maxCorrectionM * t.maxCorrectionM;
  const seedSq = t.seedMaxM * t.seedMaxM;

  return {
    get attached() {
      return attached;
    },
    get correctionM() {
      return offset.length();
    },
    step(simPos, hand, holder, dt, drawnPos) {
      // The bound. A hand this far from the simulated prop is not holding it,
      // whatever the caller derived — refuse rather than drag it across the
      // pitch. See HOLD_MAX_CORRECTION_M.
      attached = hand !== null && hand.distanceToSquared(simPos) <= maxSq;

      if (attached && hand !== null) {
        if (heldBy !== holder) {
          // THE CATCH FRAME. Seed the offset so the drawn prop does not move at
          // all, and RETURN before decaying: the sim's claim is a teleport and
          // this frame's job is to hide it, not to start closing it. The decay
          // begins on the next frame, from a picture that never jumped.
          offset.subVectors(drawnPos, simPos);
          if (offset.lengthSq() > seedSq) offset.setLength(t.seedMaxM);
          heldBy = holder;
          return out.addVectors(simPos, offset);
        }
        target.subVectors(hand, simPos);
      } else {
        heldBy = -1;
        target.set(0, 0, 0);
      }

      // Exponential approach, framerate-independent. `dt` is clamped by the
      // render shell, so the coefficient can never exceed 1 and overshoot.
      offset.lerp(target, 1 - Math.exp(-Math.max(dt, 0) / (attached ? t.gatherTauS : t.releaseTauS)));
      // Snap the last micrometre away, so "a loose prop is drawn at EXACTLY the
      // simulated position" is literally true rather than nearly true. An
      // exponential never quite arrives, and a reader has to be able to trust
      // the invariant without reasoning about floating point.
      if (!attached && offset.lengthSq() < 1e-8) offset.set(0, 0, 0);

      return out.addVectors(simPos, offset);
    },
  };
}

/** Palm centre: halfway from the wrist joint to the middle knuckle. */
function palmPoint(wrist: Object3D | null, knuckle: Object3D | null, out: Vector3): Vector3 | null {
  if (wrist === null) return null;
  wrist.updateWorldMatrix(true, false);
  out.setFromMatrixPosition(wrist.matrixWorld);
  if (knuckle === null) return out;
  knuckle.updateWorldMatrix(true, false);
  const e = knuckle.matrixWorld.elements;
  out.x = (out.x + e[12]!) * 0.5;
  out.y = (out.y + e[13]!) * 0.5;
  out.z = (out.z + e[14]!) * 0.5;
  return out;
}
